import React, { useState, useEffect, useCallback } from "react";
import PageTitle from "../../../jsx/layouts/PageTitle";
import RegistrationFilterBar from "./RegistrationFilterBar";
import RegistrationTable from "./RegistrationTable";
import RegistrationDetailsModal from "./RegistrationDetailsModal";
import useSearchValue from "../../../hooks/useSearchValue";
import { formatDateTime } from "../../../utils/dateTimeFormate";
import {
  getSportsDisciplines,
  getTrainingCentres,
  getDistricts,
  getRegistrations,
} from "../../../services/adminApiService/coeRegistrationApi/CoeRegistrationApiService";
import { EXCLUDED_EXCEL_KEYS } from "./registrationFileUtils";
import "./style.css";

const initialFilterState = {
  discipline: "",
  preferred_centre: "",
  district_id: "",
  age: "",
};

const RegistrationList = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sportsDisciplines, setSportsDisciplines] = useState([]);
  const [trainingCentres, setTrainingCentres] = useState([]);
  const [districts, setDistricts] = useState([]);

  const {
    searchValue,
    setSearchValue,
    debouncedSearchValue,
    setDebouncedSearchValue,
  } = useSearchValue(500);

  const [filters, setFilters] = useState(initialFilterState);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedRegistrationId, setSelectedRegistrationId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const response = await getSportsDisciplines();
        setSportsDisciplines(response?.data?.data || []);
      } catch (err) {
        console.error("Error loading disciplines:", err);
      }
    };
    fetchDisciplines();
  }, []);

  useEffect(() => {
    const fetchDistrictsList = async () => {
      try {
        const response = await getDistricts(4025);
        setDistricts(response?.data || []);
      } catch (err) {
        console.error("Error loading districts:", err);
      }
    };
    fetchDistrictsList();
  }, []);

  const fetchCentres = useCallback(
    async (disciplineValue) => {
      try {
        let disciplineId = null;
        if (disciplineValue) {
          const found = sportsDisciplines.find(
            (d) => d.discipline_name === disciplineValue
          );
          if (found) {
            disciplineId = found.id;
          }
        }

        const response = await getTrainingCentres(disciplineId);
        setTrainingCentres(response?.data?.data || []);
      } catch (err) {
        console.error("Error loading training centres:", err);
      }
    },
    [sportsDisciplines]
  );

  useEffect(() => {
    fetchCentres(filters.discipline);
  }, [filters.discipline, fetchCentres]);

  const fetchRegistrationsList = useCallback(
    async (filterParams, page = 1, pageSize = 10) => {
      setLoading(true);
      try {
        const response = await getRegistrations({
          search: filterParams.search,
          discipline: filterParams.discipline,
          preferred_centre: filterParams.preferred_centre,
          district_id: filterParams.district_id,
          age: filterParams.age,
          page,
          per_page: pageSize,
        });

        const paginationData = response?.data?.data;
        setRegistrations(paginationData?.data || []);
        setTotalItems(paginationData?.total || 0);
        setTotalPages(paginationData?.last_page || 1);
      } catch (err) {
        console.error("Error fetching registrations:", err);
        setRegistrations([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    fetchRegistrationsList(
      {
        search: debouncedSearchValue?.trim() || "",
        discipline: filters.discipline,
        preferred_centre: filters.preferred_centre,
        district_id: filters.district_id,
        age: filters.age,
      },
      currentPage,
      perPage
    );
  }, [
    debouncedSearchValue,
    filters.discipline,
    filters.preferred_centre,
    filters.district_id,
    filters.age,
    currentPage,
    perPage,
    fetchRegistrationsList,
  ]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "discipline") {
        updated.preferred_centre = "";
      }
      return updated;
    });
    setCurrentPage(1);
  };

  const handlePerPageChange = (size) => {
    setPerPage(size);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setSearchValue("");
    setDebouncedSearchValue("");
    setFilters(initialFilterState);
    setCurrentPage(1);
  };

  const handleViewDetails = (item) => {
    setSelectedRegistrationId(item.id);
    setShowDetailsModal(true);
  };

  const excelData = registrations.map((item, index) => {
    const row = {
      serial: (currentPage - 1) * perPage + index + 1,
      ...item,
      dob: item.dob ? formatDateTime(item.dob) : "NA",
    };
    EXCLUDED_EXCEL_KEYS.forEach((k) => {
      delete row[k];
    });
    Object.keys(row).forEach((k) => {
      if (
        row[k] === null ||
        row[k] === undefined ||
        (typeof row[k] === "string" && row[k].trim() === "")
      ) {
        row[k] = "NA";
      }
    });
    return row;
  });

  return (
    <>
      <PageTitle
        motherMenu="Manage Registration"
        activeMenu="Registration"
      />

      <div className="col-12">
        <div className="card registration-card">
          <RegistrationFilterBar
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            perPage={perPage}
            onPerPageChange={handlePerPageChange}
            filters={filters}
            onFilterChange={handleFilterChange}
            sportsDisciplines={sportsDisciplines}
            trainingCentres={trainingCentres}
            districts={districts}
            onRefresh={handleRefresh}
            excelData={excelData}
          />

          <RegistrationTable
            data={registrations}
            loading={loading}
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onViewDetails={handleViewDetails}
          />
        </div>
      </div>

      <RegistrationDetailsModal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setSelectedRegistrationId(null);
        }}
        registrationId={selectedRegistrationId}
      />
    </>
  );
};

export default RegistrationList;
